import { emptySplitApi as api } from "./emptyApi"

/** Per-section chart rendering: line/grouped-bar trend, or stacked-bar trend. */
export type ReportChartType = "bar" | "line" | "stack"

/** One report section: a named, ordered, cube-query-backed panel. */
export type CustomReportSection = {
  name: string
  query: Record<string, unknown>
  order: number
  /** How the section's chart renders. Absent = legacy adaptive default. */
  chartType?: ReportChartType
}

export type CustomReportDocument = {
  reports: CustomReportSection[]
}

export type CompanyCustomReport = {
  id: string
  companyId: string
  name: string
  reports: CustomReportDocument
  createdAt: string
  updatedAt: string
}

export type SaveCustomReportBody = {
  name: string
  reports: CustomReportDocument
}

const LIST_ID = "LIST" as const

export const companyCustomReportApi = api.injectEndpoints({
  endpoints: build => ({
    getCompanyCustomReports: build.query<CompanyCustomReport[], void>({
      query: () => ({ url: "/company-custom-report" }),
      providesTags: result => [
        { type: "CompanyCustomReport" as const, id: LIST_ID },
        ...(result ?? []).map(r => ({
          type: "CompanyCustomReport" as const,
          id: r.id,
        })),
      ],
    }),
    getCompanyCustomReport: build.query<CompanyCustomReport, string>({
      query: id => ({ url: `/company-custom-report/${id}` }),
      providesTags: (_result, _err, id) => [
        { type: "CompanyCustomReport", id },
      ],
    }),
    createCompanyCustomReport: build.mutation<
      CompanyCustomReport,
      SaveCustomReportBody
    >({
      query: body => ({
        url: "/company-custom-report",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "CompanyCustomReport", id: LIST_ID }],
    }),
    updateCompanyCustomReport: build.mutation<
      CompanyCustomReport,
      { id: string; body: Partial<SaveCustomReportBody> }
    >({
      query: ({ id, body }) => ({
        url: `/company-custom-report/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        { type: "CompanyCustomReport", id },
        { type: "CompanyCustomReport", id: LIST_ID },
      ],
    }),
    deleteCompanyCustomReport: build.mutation<CompanyCustomReport, string>({
      query: id => ({
        url: `/company-custom-report/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "CompanyCustomReport", id: LIST_ID }],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetCompanyCustomReportsQuery,
  useGetCompanyCustomReportQuery,
  useCreateCompanyCustomReportMutation,
  useUpdateCompanyCustomReportMutation,
  useDeleteCompanyCustomReportMutation,
} = companyCustomReportApi
